import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Dumbbell, 
  ArrowLeft,
  Play,
  Pause,
  SkipForward,
  Check,
  Clock,
  Flame,
  Trophy,
  Sparkles,
  Timer,
  RotateCcw,
  Video,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function WorkoutDetailPage() {
  const { workoutId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [restTimer, setRestTimer] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [workoutStartTime, setWorkoutStartTime] = useState(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [workoutStats, setWorkoutStats] = useState({ duration: 0, exercisesCompleted: 0, setsCompleted: 0 });
  const [expandedVideo, setExpandedVideo] = useState(null);

  const fetchWorkout = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workouts/${workoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkout(response.data);
    } catch (error) {
      toast.error('Workout not found');
      navigate('/workouts');
    } finally {
      setLoading(false);
    }
  }, [workoutId, token, navigate]);

  useEffect(() => {
    fetchWorkout();
  }, [fetchWorkout]);

  // Rest timer effect
  useEffect(() => {
    let interval = null;
    if (isResting && restTimer > 0) {
      interval = setInterval(() => {
        setRestTimer(prev => {
          if (prev <= 1) {
            setIsResting(false);
            toast.success('Rest complete! Ready for next set');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isResting, restTimer]);

  const startWorkout = () => {
    setIsActive(true);
    setWorkoutStartTime(new Date());
    setCurrentExerciseIndex(0);
    setCurrentSet(1);
    setCompletedExercises([]);
    toast.success("Let's go! Workout started");
  };

  const completeSet = () => {
    const exercise = workout.exercises[currentExerciseIndex];
    const totalSets = exercise.sets || 3;
    
    if (currentSet < totalSets) {
      // Start rest timer
      setRestTimer(exercise.rest_seconds || 60);
      setIsResting(true);
      setCurrentSet(prev => prev + 1);
    } else {
      // Exercise complete
      setCompletedExercises(prev => [...prev, currentExerciseIndex]);
      
      if (currentExerciseIndex < workout.exercises.length - 1) {
        // Move to next exercise
        setCurrentExerciseIndex(prev => prev + 1);
        setCurrentSet(1);
        toast.success(`${exercise.name} complete! Moving to next exercise`);
      } else {
        // Workout complete!
        finishWorkout();
      }
    }
  };

  const skipRest = () => {
    setIsResting(false);
    setRestTimer(0);
  };

  const finishWorkout = async () => {
    const endTime = new Date();
    const duration = Math.floor((endTime - workoutStartTime) / 1000 / 60);
    
    const stats = {
      duration,
      exercisesCompleted: completedExercises.length + 1,
      setsCompleted: workout.exercises.reduce((acc, ex, idx) => {
        if (idx < currentExerciseIndex) return acc + (ex.sets || 3);
        if (idx === currentExerciseIndex) return acc + currentSet;
        return acc;
      }, 0)
    };
    
    setWorkoutStats(stats);
    setIsActive(false);
    setShowCompletionDialog(true);

    // Log activity
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(
        `${API}/activity`,
        null,
        {
          params: {
            date: today,
            workouts_completed: 1,
            active_minutes: duration,
            calories_burned: duration * 8
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!workout) return null;

  const currentExercise = workout.exercises[currentExerciseIndex];
  const progress = workout.exercises.length > 0 
    ? ((completedExercises.length) / workout.exercises.length) * 100 
    : 0;

  return (
    <div data-testid="workout-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => navigate('/workouts')}
          className="border-border hover:border-primary"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">
            {workout.title}
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <Dumbbell className="w-4 h-4" />
              {workout.exercises?.length || 0} exercises
            </span>
            {workout.is_ai_generated && (
              <span className="text-sm text-primary flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                AI Generated
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Active Workout View */}
      {isActive ? (
        <div className="space-y-6">
          {/* Progress Bar */}
          <div className="card-tactical">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Workout Progress</span>
              <span className="text-sm text-white font-mono">
                {completedExercises.length}/{workout.exercises.length} exercises
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Rest Timer */}
          {isResting && (
            <div className="card-tactical border-primary bg-primary/10 text-center py-8">
              <div className="text-sm text-gray-400 uppercase tracking-wider mb-2">Rest Time</div>
              <div className="font-mono text-6xl text-white mb-4">{formatTime(restTimer)}</div>
              <Button 
                onClick={skipRest}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                Skip Rest
              </Button>
            </div>
          )}

          {/* Current Exercise */}
          {!isResting && currentExercise && (
            <div className="card-tactical">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400 uppercase tracking-wider">
                  Exercise {currentExerciseIndex + 1} of {workout.exercises.length}
                </span>
                <span className="text-xs text-primary font-mono">
                  Set {currentSet}/{currentExercise.sets || 3}
                </span>
              </div>
              
              <h2 className="font-heading text-3xl text-white uppercase mb-4">
                {currentExercise.name}
              </h2>
              
              {/* Video Demo */}
              {currentExercise.video_url && (
                <div className="mb-4">
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-secondary">
                    <iframe
                      src={currentExercise.video_url}
                      title={`${currentExercise.name} demo`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Watch proper form before performing
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-secondary/50 rounded-sm">
                  <div className="text-2xl font-mono text-white">{currentExercise.sets || 3}</div>
                  <div className="text-xs text-gray-400 uppercase">Sets</div>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-sm">
                  <div className="text-2xl font-mono text-white">{currentExercise.reps || 12}</div>
                  <div className="text-xs text-gray-400 uppercase">Reps</div>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-sm">
                  <div className="text-2xl font-mono text-white">{currentExercise.rest_seconds || 60}s</div>
                  <div className="text-xs text-gray-400 uppercase">Rest</div>
                </div>
              </div>
              
              {currentExercise.instructions && (
                <div className="p-4 bg-secondary/30 rounded-sm mb-6">
                  <div className="text-xs text-gray-400 uppercase mb-2">Instructions</div>
                  <p className="text-gray-300 text-sm">{currentExercise.instructions}</p>
                </div>
              )}
              
              <Button 
                data-testid="complete-set-btn"
                onClick={completeSet}
                className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold py-6 text-lg"
              >
                <Check className="w-5 h-5 mr-2" />
                Complete Set {currentSet}
              </Button>
            </div>
          )}

          {/* Exercise List */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4">All Exercises</h3>
            <div className="space-y-2">
              {workout.exercises.map((exercise, index) => {
                const isCompleted = completedExercises.includes(index);
                const isCurrent = index === currentExerciseIndex;
                
                return (
                  <div 
                    key={index}
                    className={`flex items-center gap-4 p-3 rounded-sm border ${
                      isCompleted ? 'border-green-500 bg-green-500/10' :
                      isCurrent ? 'border-primary bg-primary/10' :
                      'border-border bg-secondary/30'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500' :
                      isCurrent ? 'bg-primary' :
                      'bg-gray-700'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <span className="text-white text-sm font-mono">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`font-medium ${isCompleted ? 'text-gray-400 line-through' : 'text-white'}`}>
                        {exercise.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {exercise.sets || 3} sets × {exercise.reps || 12} reps
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Pre-Workout View */
        <div className="space-y-6">
          {/* Workout Info */}
          <div className="card-tactical">
            <p className="text-gray-400 mb-4">{workout.description}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-secondary/50 rounded-sm">
                <Dumbbell className="w-6 h-6 text-primary mx-auto mb-2" />
                <div className="text-xl font-mono text-white">{workout.exercises?.length || 0}</div>
                <div className="text-xs text-gray-400 uppercase">Exercises</div>
              </div>
              <div className="text-center p-4 bg-secondary/50 rounded-sm">
                <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <div className="text-xl font-mono text-white">{workout.duration_weeks || 1}</div>
                <div className="text-xs text-gray-400 uppercase">Weeks</div>
              </div>
              <div className="text-center p-4 bg-secondary/50 rounded-sm">
                <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <div className="text-xl font-mono text-white capitalize">{workout.difficulty}</div>
                <div className="text-xs text-gray-400 uppercase">Level</div>
              </div>
              <div className="text-center p-4 bg-secondary/50 rounded-sm">
                <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <div className="text-xl font-mono text-white capitalize">{workout.category}</div>
                <div className="text-xs text-gray-400 uppercase">Category</div>
              </div>
            </div>
            
            <Button 
              data-testid="start-workout-btn"
              onClick={startWorkout}
              disabled={!workout.exercises?.length}
              className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold py-6 text-lg"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Workout
            </Button>
          </div>

          {/* Exercise List */}
          {workout.exercises?.length > 0 && (
            <div className="card-tactical">
              <h3 className="font-heading text-lg text-white uppercase mb-4">Exercises</h3>
              <div className="space-y-3">
                {workout.exercises.map((exercise, index) => (
                  <div 
                    key={index}
                    className="bg-secondary/30 rounded-sm border border-border overflow-hidden"
                  >
                    <div 
                      className="flex items-center gap-4 p-4 cursor-pointer"
                      onClick={() => exercise.video_url && setExpandedVideo(expandedVideo === index ? null : index)}
                    >
                      <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center">
                        <span className="font-mono text-primary font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium flex items-center gap-2">
                          {exercise.name}
                          {exercise.video_url && (
                            <Video className="w-4 h-4 text-blue-400" />
                          )}
                        </div>
                        <div className="text-sm text-gray-400">
                          {exercise.sets || 3} sets × {exercise.reps || 12} reps • {exercise.rest_seconds || 60}s rest
                        </div>
                        {exercise.muscle_groups && (
                          <div className="flex gap-2 mt-1">
                            {(Array.isArray(exercise.muscle_groups) ? exercise.muscle_groups : [exercise.muscle_groups]).map((muscle, i) => (
                              <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded text-gray-400">
                                {muscle}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {exercise.video_url && (
                        <div className="text-gray-400">
                          {expandedVideo === index ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      )}
                    </div>
                    
                    {/* Expandable Video */}
                    {exercise.video_url && expandedVideo === index && (
                      <div className="px-4 pb-4">
                        <div className="relative aspect-video rounded-sm overflow-hidden bg-black">
                          <iframe
                            src={exercise.video_url}
                            title={`${exercise.name} demo`}
                            className="absolute inset-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Watch this video to learn proper form
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completion Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent className="bg-card border-border text-center">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl text-white uppercase">
              Workout <span className="text-primary">Complete!</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-8">
            <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-12 h-12 text-primary" />
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center">
                <div className="text-3xl font-mono text-white">{workoutStats.duration}</div>
                <div className="text-xs text-gray-400 uppercase">Minutes</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-mono text-white">{workoutStats.exercisesCompleted}</div>
                <div className="text-xs text-gray-400 uppercase">Exercises</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-mono text-white">{workoutStats.setsCompleted}</div>
                <div className="text-xs text-gray-400 uppercase">Sets</div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-border"
                onClick={() => {
                  setShowCompletionDialog(false);
                  startWorkout();
                }}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Repeat
              </Button>
              <Button
                className="flex-1 bg-primary hover:bg-red-700"
                onClick={() => navigate('/workouts')}
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
